import * as React from 'react';
import { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import NumberSelector from '../../../common/ui/NumberSelector';
import { AccountData, OrderData, OrderService } from '../../../api';
import { getTimeFormat } from '../../../common/getDateAndTimeFormat';
import { useAuth } from '../../../session/InternalAuthProvider';

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(grey[500]),
  '&:hover': {
    color: grey[700],
  },
}));

type CheckinDialogProps = {
  order?: OrderData;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  updateItem: (itemToUpdate: OrderData) => void;
};
export default function CheckinDialog({ order, isOpen, setIsOpen, updateItem }: CheckinDialogProps) {
  const [loading, setLoading] = useState(false);
  const [persons, setPersons] = useState(0);
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account || undefined);
  const [comment, setComment] = useState(order?.comment);
  const onSaveHandler = async () => {
    setLoading(true);
    if (order) {
      const checkedInOrder = await OrderService.checkinOrder({
        order_id: order.id,
        persons,
        comment,
      });

      updateItem({ ...checkedInOrder });
    }

    setIsOpen(false);
    setLoading(false);
  };

  useMemo(() => {
    if (order) {
      let defaultPersons = order.persons;
      if (order && order.checkin) {
        defaultPersons = order.checkin.persons;
      }
      setPersons(defaultPersons);
      setComment(order.comment);
    }
  }, [order]);

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{t('checkin_dialog.title')}</DialogTitle>
        {order && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {order.persons} {order.persons === 1 ? t('checkin_dialog.person') : t('checkin_dialog.people')} •{' '}
              {order.time ? moment.unix(order.time).tz('utc').format(timeFormat) : ''} • {order.firstname}{' '}
              {order.lastname}
            </Typography>
          </Box>
        )}
        <DialogContent>
          {order && (
            <Grid container flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
              <NumberSelector value={persons} setValue={setPersons} disabled={order.persons === 1} min={0} />
              <TextField
                multiline
                fullWidth
                label={t('checkin_dialog.comments')}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <ColorButton onClick={() => setIsOpen(false)}>{t('cancel')}</ColorButton>
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={onSaveHandler}
            sx={{ boxShadow: 'none' }}
            autoFocus>
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
